import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LoginBg from '../LoginBg';
import Logo from '../../assets/logo-vert-white.png';
import { userService } from '../../services/userService';
import { firePublicSwal } from '../../utils/configureSwal';
import {
  PageContainer,
  Form,
  Label,
  Input,
  Img,
  ErrorText,
  HintText,
  RuleList,
  RuleItem,
  StyledButtonConfirmar,
  StyledButtonVoltar,
} from './styles';

const PASSWORD_RULES = [
  { key: 'length', label: 'Mínimo de 8 caracteres', test: (p) => p.length >= 8 },
  { key: 'not-numeric', label: 'Não pode ser inteiramente numérica', test: (p) => p.length > 0 && !/^\d+$/.test(p) },
];

function extractApiErrors(data) {
  if (!data) return null;

  if (data.detail) return data.detail;

  const fieldMessages = [];
  for (const key of ['password', 'password2', 'non_field_errors']) {
    if (data[key]) {
      fieldMessages.push(...[].concat(data[key]));
    }
  }
  if (fieldMessages.length > 0) return fieldMessages.join(' ');

  if (data.error) return data.error;
  if (data.message) return data.message;

  return null;
}

const RecoverPassword = () => {
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { code, hash } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.history.replaceState(null, '', '/recover_password');
  }, []);

  const rulesStatus = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, valid: r.test(password) })),
    [password],
  );

  const allRulesPass = rulesStatus.every((r) => r.valid);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!allRulesPass) {
      setFormError('A senha não atende a todos os requisitos.');
      return;
    }

    if (password !== password2) {
      setFormError('As senhas não coincidem.');
      return;
    }

    setSubmitting(true);
    try {
      await userService.confirmPasswordReset(code, hash, password, password2);

      firePublicSwal({
        title: 'Senha alterada com sucesso.',
        text: 'Agora você pode fazer login com sua nova senha.',
        icon: 'success',
        confirmButtonText: 'Ir para o login',
      }).then(() => {
        navigate('/');
      });
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      let message;

      if (status === 400) {
        message = extractApiErrors(data)
          || 'Link inválido ou expirado.';
      } else {
        message = 'Não foi possível redefinir a senha. Tente novamente mais tarde.';
      }

      firePublicSwal({
        title: 'Erro',
        text: message,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <LoginBg />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <PageContainer>
          <div style={{ overflow: 'hidden' }}>
            <Img
              src={Logo}
              alt="Vert Ecotech"
              style={{
                filter: 'drop-shadow(3px 3px 1px rgba(255, 255, 255, 0.9))',
                borderRadius: '10px',
              }}
            />
          </div>

          <Form onSubmit={handleSubmit}>
            <Label>Nova senha</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />

            {password.length > 0 && (
              <>
                <RuleList>
                  {rulesStatus.map((r) => (
                    <RuleItem key={r.key} $valid={r.valid}>
                      {r.label}
                    </RuleItem>
                  ))}
                </RuleList>
                <HintText>
                  Evite senhas comuns ou muito parecidas com seus dados pessoais.
                </HintText>
              </>
            )}

            <Label>Confirmar nova senha</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={password2}
              autoComplete="new-password"
              onChange={(e) => setPassword2(e.target.value)}
            />

            {formError && <ErrorText>{formError}</ErrorText>}

            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <StyledButtonConfirmar type="submit" disabled={submitting}>
                {submitting ? 'Redefinindo...' : 'Resetar Senha'}
              </StyledButtonConfirmar>
              <StyledButtonVoltar type="button" onClick={() => navigate('/')}>
                Voltar ao Login
              </StyledButtonVoltar>
            </div>
          </Form>
        </PageContainer>
      </motion.div>
    </>
  );
};

export default RecoverPassword;
